import { useQuery } from "@tanstack/react-query";
import { abcCurve } from "@/lib/reports/abc";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { subYears } from "date-fns";

export type AgeGroup = "under1" | "1to7" | "7plus" | "unknown";
export type DetailedAgeGroup = "puppy" | "adult" | "senior" | "geriatric" | "unknown";
export type ExplorerAbcCurve = "A" | "B" | "C" | "none";
export type RelationshipStatus = "new" | "active" | "recurring" | "atRisk" | "dormant" | "lost";

export interface ExplorerPet {
  petId: string;
  petName: string;
  speciesId: string | null;
  speciesName: string | null;
  breedId: string | null;
  breedName: string | null;
  coatTypeId: string | null;
  coatTypeName: string | null;
  sizeCategoryId: string | null;
  sizeCategoryName: string | null;
  genderId: string | null;
  genderName: string | null;
  birthDate: Date | null;
  ageYears: number | null;
  ageGroup: AgeGroup;
  detailedAgeGroup: DetailedAgeGroup;
  sterilizationId: string | null;
  sterilizationName: string | null;
  isSterilized: boolean | null;
  currentWeight: number | null;
  weightMeasuredAt: Date | null;
  daysSinceWeightUpdate: number | null;
  conditionNames: string[];
  allergyNames: string[];
  temperament: string | null;
  lastConsultationDate: Date | null;
  nextApplicationDate: Date | null;
  lastApplicationDate: Date | null;
  isAlive: boolean;
}

export interface ExplorerRow {
  ownerId: string;
  ownerName: string;
  createdAt: Date | null;
  marketingConsent: boolean | null;
  hasPhone: boolean;
  hasWhatsApp: boolean;
  primaryPhone: string | null;
  neighborhood: string | null;
  cityName: string | null;
  stateCode: string | null;
  pets: ExplorerPet[];
  lastVisitDate: Date | null;
  daysSinceLastVisit: number | null;
  visitCount: number;
  totalHistoricalSpent: number;
  saleCount: number;
  averageTicket: number;
  relationshipStatus: RelationshipStatus;
  abcCurve: ExplorerAbcCurve;
  cumulativePct: number;
}

function computeAgeGroup(birthDate: string | null): AgeGroup {
  if (!birthDate) return "unknown";
  const ageYears = computeAgeYears(birthDate);
  if (ageYears === null) return "unknown";
  if (ageYears < 1) return "under1";
  if (ageYears <= 7) return "1to7";
  return "7plus";
}

function computeAgeYears(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  const ageMs = now.getTime() - birth.getTime();
  return Math.max(0, ageMs / (1000 * 60 * 60 * 24 * 365.25));
}

function normalizeName(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function computeDetailedAgeGroup(ageYears: number | null): DetailedAgeGroup {
  if (ageYears === null) return "unknown";
  if (ageYears < 1) return "puppy";
  if (ageYears < 7) return "adult";
  if (ageYears < 10) return "senior";
  return "geriatric";
}

function computeRelationshipStatus(params: {
  createdAt: Date | null;
  daysSinceLastVisit: number | null;
  visitCount: number;
}): RelationshipStatus {
  if (params.daysSinceLastVisit === null) return "new";
  if (params.daysSinceLastVisit >= 365) return "lost";
  if (params.daysSinceLastVisit >= 180) return "dormant";
  if (params.daysSinceLastVisit >= 90) return "atRisk";
  if (params.visitCount >= 3) return "recurring";
  return "active";
}

function computeAbcCurve(rows: { totalHistoricalSpent: number }[]): { abcCurve: ExplorerAbcCurve; cumulativePct: number }[] {
  const total = rows.reduce((s, r) => s + r.totalHistoricalSpent, 0);
  if (total === 0)
    return rows.map(() => ({ abcCurve: "none" as ExplorerAbcCurve, cumulativePct: 0 }));
  // a lista chega ordenada por gasto (maior primeiro), como a curva pede
  return abcCurve(rows.map((r) => r.totalHistoricalSpent)).map((c) => ({ abcCurve: c.abc as ExplorerAbcCurve, cumulativePct: c.cumulativeShare }));
}

export function useBaseExplorer() {
  const { data: clinicId } = useCurrentClinicId();
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["base-explorer", clinicId, customerId],
    queryFn: async (): Promise<ExplorerRow[]> => {
      if (!clinicId || !customerId) return [];

      // All owners linked to this customer
      const { data: ownerLinks, error: ownerLinksErr } = await supabase
        .from("owners_customers")
        .select("owner_id")
        .eq("customer_id", customerId)
        .is("deleted_at", null);

      if (ownerLinksErr) throw ownerLinksErr;
      const ownerIds = (ownerLinks ?? []).map((r) => r.owner_id);
      if (ownerIds.length === 0) return [];

      // Fetch in parallel: owner names, pets, phones, last visit, total spend
      const [ownersRes, petsRes, phonesRes, appointmentsRes, salesRes] = await Promise.all([
        supabase
          .from("owners")
          .select("id, name, created_at, marketing_consent")
          .in("id", ownerIds)
          .is("deleted_at", null),

        supabase
          .from("pets")
          .select(
            "id, name, owner_id, species_id, breed_id, coat_type_id, gender_id, birth_date, sterilization_id, death_date, size_category_id, species:species_id(id, name), breed:breed_id(id, name), coat_type:coat_types(id, name), gender:pet_genders(id, name), sterilization:pet_sterilizations(id, name), size_category:pet_size_categories(id, name), grooming_profile:pet_grooming_profiles(temperament)"
          )
          .in("owner_id", ownerIds)
          .is("deleted_at", null),

        supabase
          .from("owners_phones")
          .select("owner_id, phone_number, is_primary, is_whatsapp")
          .in("owner_id", ownerIds)
          .is("deleted_at", null),

        supabase
          .from("appointments")
          .select("owner_id, pet_id, starts_at")
          .eq("clinic_id", clinicId)
          .in("owner_id", ownerIds)
          .gte("starts_at", subYears(new Date(), 3).toISOString())
          .not("status", "in", "(cancelado,faltou)")
          .not("owner_id", "is", null),

        supabase
          .from("sales")
          .select("owner_id, total")
          .eq("clinic_id", clinicId)
          .eq("status", "PAGA")
          .in("owner_id", ownerIds),
      ]);

      // Build lookup maps
      const ownerNameMap: Record<string, string> = {};
      (ownersRes.data ?? []).forEach((o) => {
        ownerNameMap[o.id] = o.name ?? "Sem nome";
      });

      const ownerMetaMap: Record<string, { createdAt: Date | null; marketingConsent: boolean | null }> = {};
      (ownersRes.data ?? []).forEach((o) => {
        ownerMetaMap[o.id] = {
          createdAt: o.created_at ? new Date(o.created_at) : null,
          marketingConsent: o.marketing_consent ?? null,
        };
      });

      // Phones
      const phonesMap: Record<string, { hasPhone: boolean; hasWhatsApp: boolean; primaryPhone: string | null }> = {};
      (phonesRes.data ?? []).forEach((p) => {
        if (!phonesMap[p.owner_id]) {
          phonesMap[p.owner_id] = { hasPhone: false, hasWhatsApp: false, primaryPhone: null };
        }
        phonesMap[p.owner_id].hasPhone = true;
        if (p.is_whatsapp) phonesMap[p.owner_id].hasWhatsApp = true;
        if (p.is_primary || !phonesMap[p.owner_id].primaryPhone) {
          phonesMap[p.owner_id].primaryPhone = p.phone_number;
        }
      });

      const { data: addresses } = await supabase
        .from("owners_addresses")
        .select("owner_id, neighborhood, is_primary, city:cities(name, state:states(code))")
        .in("owner_id", ownerIds)
        .is("deleted_at", null);
      type AddressRow = {
        owner_id: string;
        neighborhood: string | null;
        is_primary: boolean | null;
        city: { name: string; state: { code: string } | null } | null;
      };
      const addressMap: Record<string, { neighborhood: string | null; cityName: string | null; stateCode: string | null }> = {};
      ((addresses ?? []) as AddressRow[])
        .sort((a, b) => Number(Boolean(b.is_primary)) - Number(Boolean(a.is_primary)))
        .forEach((address) => {
          if (addressMap[address.owner_id]) return;
          addressMap[address.owner_id] = {
            neighborhood: address.neighborhood,
            cityName: address.city?.name ?? null,
            stateCode: address.city?.state?.code ?? null,
          };
        });

      // Pets
      type PetRow = {
        id: string;
        name: string;
        owner_id: string;
        species_id: string | null;
        breed_id: string | null;
        coat_type_id: string | null;
        gender_id: string | null;
        birth_date: string | null;
        sterilization_id: string | null;
        death_date: string | null;
        species: { id: string; name: string } | null;
        breed: { id: string; name: string } | null;
        coat_type: { id: string; name: string } | null;
        gender: { id: string; name: string } | null;
        sterilization: { id: string; name: string } | null;
        size_category_id: string | null;
        size_category: { id: string; name: string } | null;
        grooming_profile:
          | {
              temperament?: string | null;
            }
          | Array<{
              temperament?: string | null;
            }>
          | null;
      };
      const petIds = ((petsRes.data ?? []) as PetRow[]).map((p) => p.id);
      const [weightsRes, conditionsRes, applicationPlansRes] = petIds.length > 0
        ? await Promise.all([
            supabase
              .from("pet_weights")
              .select("pet_id, weight, measured_at, created_at")
              .in("pet_id", petIds)
              .is("deleted_at", null)
              .order("measured_at", { ascending: false }),
            supabase
              .from("pet_conditions")
              .select("pet_id, condition:conditions(name, type)")
              .in("pet_id", petIds),
            supabase
              .from("pet_application_plans")
              .select("pet_id, due_date, status")
              .in("pet_id", petIds)
              .is("deleted_at", null),
          ])
        : [{ data: [] }, { data: [] }, { data: [] }];

      const weightMap: Record<string, { weight: number; measuredAt: Date | null }> = {};
      type WeightRow = { pet_id: string; weight: number; measured_at: string | null; created_at: string | null };
      ((weightsRes.data ?? []) as WeightRow[]).forEach((row) => {
        if (weightMap[row.pet_id]) return;
        weightMap[row.pet_id] = {
          weight: Number(row.weight),
          measuredAt: row.measured_at ? new Date(row.measured_at) : row.created_at ? new Date(row.created_at) : null,
        };
      });

      type ConditionRow = { pet_id: string; condition: { name: string; type: string } | null };
      const conditionMap: Record<string, { conditions: string[]; allergies: string[] }> = {};
      ((conditionsRes.data ?? []) as ConditionRow[]).forEach((row) => {
        if (!conditionMap[row.pet_id]) conditionMap[row.pet_id] = { conditions: [], allergies: [] };
        if (!row.condition?.name) return;
        const type = row.condition.type.toLowerCase();
        if (type.includes("alerg")) conditionMap[row.pet_id].allergies.push(row.condition.name);
        else conditionMap[row.pet_id].conditions.push(row.condition.name);
      });

      type ApplicationPlanRow = { pet_id: string; due_date: string | null; status: string | null };
      const applicationMap: Record<string, { last: Date | null; next: Date | null }> = {};
      const nowForApplications = new Date();
      ((applicationPlansRes.data ?? []) as ApplicationPlanRow[]).forEach((row) => {
        if (!row.due_date) return;
        if (!applicationMap[row.pet_id]) applicationMap[row.pet_id] = { last: null, next: null };
        const dueDate = new Date(row.due_date);
        const status = (row.status ?? "").toLowerCase();
        if (status === "completed") {
          if (!applicationMap[row.pet_id].last || dueDate > applicationMap[row.pet_id].last) {
            applicationMap[row.pet_id].last = dueDate;
          }
        } else if (dueDate >= nowForApplications) {
          if (!applicationMap[row.pet_id].next || dueDate < applicationMap[row.pet_id].next) {
            applicationMap[row.pet_id].next = dueDate;
          }
        }
      });

      const petsMap: Record<string, ExplorerPet[]> = {};
      ((petsRes.data ?? []) as PetRow[]).forEach((p: PetRow) => {
        const groomingProfile = Array.isArray(p.grooming_profile)
          ? p.grooming_profile[0] ?? null
          : p.grooming_profile;
        if (!petsMap[p.owner_id]) petsMap[p.owner_id] = [];
        const ageYears = computeAgeYears(p.birth_date);
        const weight = weightMap[p.id] ?? null;
        const daysSinceWeightUpdate = weight?.measuredAt
          ? Math.floor((new Date().getTime() - weight.measuredAt.getTime()) / (1000 * 60 * 60 * 24))
          : null;
        petsMap[p.owner_id].push({
          petId: p.id,
          petName: p.name ?? "Pet",
          speciesId: p.species_id,
          speciesName: p.species?.name ?? null,
          breedId: p.breed_id,
          breedName: p.breed?.name ?? null,
          coatTypeId: p.coat_type_id,
          coatTypeName: p.coat_type?.name ?? null,
          sizeCategoryId: p.size_category_id ?? null,
          sizeCategoryName: p.size_category?.name ?? null,
          genderId: p.gender_id,
          genderName: p.gender?.name ?? null,
          birthDate: p.birth_date ? new Date(p.birth_date) : null,
          ageGroup: computeAgeGroup(p.birth_date),
          sterilizationId: p.sterilization_id,
          sterilizationName: p.sterilization?.name ?? null,
          isSterilized: p.sterilization?.name ? normalizeName(p.sterilization.name).includes("castrad") : null,
          ageYears,
          detailedAgeGroup: computeDetailedAgeGroup(ageYears),
          currentWeight: weight?.weight ?? null,
          weightMeasuredAt: weight?.measuredAt ?? null,
          daysSinceWeightUpdate,
          conditionNames: conditionMap[p.id]?.conditions ?? [],
          allergyNames: conditionMap[p.id]?.allergies ?? [],
          temperament: groomingProfile?.temperament ?? null,
          lastConsultationDate: null,
          nextApplicationDate: applicationMap[p.id]?.next ?? null,
          lastApplicationDate: applicationMap[p.id]?.last ?? null,
          isAlive: !p.death_date,
        });
      });

      // Last visit
      const lastVisitMap: Record<string, Date> = {};
      const visitCountMap: Record<string, number> = {};
      const petLastConsultationMap: Record<string, Date> = {};
      (appointmentsRes.data ?? []).forEach((a) => {
        if (!a.owner_id) return;
        const d = new Date(a.starts_at);
        visitCountMap[a.owner_id] = (visitCountMap[a.owner_id] ?? 0) + 1;
        if (!lastVisitMap[a.owner_id] || d > lastVisitMap[a.owner_id]) {
          lastVisitMap[a.owner_id] = d;
        }
        if (a.pet_id && (!petLastConsultationMap[a.pet_id] || d > petLastConsultationMap[a.pet_id])) {
          petLastConsultationMap[a.pet_id] = d;
        }
      });
      Object.values(petsMap).forEach((pets) => {
        pets.forEach((pet) => {
          pet.lastConsultationDate = petLastConsultationMap[pet.petId] ?? null;
        });
      });

      // Sales / total spend
      const spendMap: Record<string, number> = {};
      const saleCountMap: Record<string, number> = {};
      (salesRes.data ?? []).forEach((s) => {
        if (!s.owner_id) return;
        spendMap[s.owner_id] = (spendMap[s.owner_id] ?? 0) + Number(s.total ?? 0);
        saleCountMap[s.owner_id] = (saleCountMap[s.owner_id] ?? 0) + 1;
      });

      const now = new Date();
      const dayMs = 1000 * 60 * 60 * 24;

      // Build raw rows (only owners that exist in nameMap = linked & not deleted)
      const rawRows = ownerIds
        .filter((id) => ownerNameMap[id])
        .map((id) => {
          const visitCount = visitCountMap[id] ?? 0;
          const saleCount = saleCountMap[id] ?? 0;
          const totalHistoricalSpent = spendMap[id] ?? 0;
          const daysSinceLastVisit = lastVisitMap[id]
            ? Math.floor((now.getTime() - lastVisitMap[id].getTime()) / dayMs)
            : null;
          return {
            ownerId: id,
            ownerName: ownerNameMap[id],
            createdAt: ownerMetaMap[id]?.createdAt ?? null,
            marketingConsent: ownerMetaMap[id]?.marketingConsent ?? null,
            hasPhone: phonesMap[id]?.hasPhone ?? false,
            hasWhatsApp: phonesMap[id]?.hasWhatsApp ?? false,
            primaryPhone: phonesMap[id]?.primaryPhone ?? null,
            neighborhood: addressMap[id]?.neighborhood ?? null,
            cityName: addressMap[id]?.cityName ?? null,
            stateCode: addressMap[id]?.stateCode ?? null,
            pets: petsMap[id] ?? [],
            lastVisitDate: lastVisitMap[id] ?? null,
            daysSinceLastVisit,
            visitCount,
            totalHistoricalSpent,
            saleCount,
            averageTicket: saleCount > 0 ? totalHistoricalSpent / saleCount : 0,
            relationshipStatus: computeRelationshipStatus({
              createdAt: ownerMetaMap[id]?.createdAt ?? null,
              daysSinceLastVisit,
              visitCount,
            }),
            abcCurve: "none" as ExplorerAbcCurve,
            cumulativePct: 0,
          };
        });

      // Sort by spend desc, then apply ABC
      rawRows.sort((a, b) => b.totalHistoricalSpent - a.totalHistoricalSpent);
      const abcCurves = computeAbcCurve(rawRows);
      return rawRows.map((r, i) => ({ ...r, ...abcCurves[i] }));
    },
    enabled: !!clinicId && !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}
